import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import PageHero from '../components/PageHero';
import SEO from '../components/SEO';
import { useFavorites } from '../context/FavoritesContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Favorite properties page — keeps return visitors coming back. Loads each
 * favorite individually because /api/xml/properties returns the whole feed
 * (435 entries) which is wasteful when we only want 3 specific IDs.
 */
const Favorites = () => {
  const { favorites } = useFavorites();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (favorites.length === 0) { setProperties([]); setLoading(false); return; }
    setLoading(true);
    // Cheapest path: fetch the whole feed once and filter — the dataset is
    // already cached server-side so this is one round-trip, not N.
    fetch(`${API}/api/xml/properties?limit=500&lang=pl`)
      .then(r => r.json())
      .then(all => {
        const wanted = new Set(favorites);
        setProperties(all.filter(p => wanted.has(p.id)));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [favorites]);

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '4rem' }}>
      <SEO titleKey="seo.propertiesTitle" descriptionKey="seo.propertiesDescription" noindex />
      <PageHero
        image="https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1920&q=80"
        title="Moje ulubione"
        subtitle={`${favorites.length} ${favorites.length === 1 ? 'zapisana nieruchomość' : 'zapisanych nieruchomości'}`}
        testIdTitle="favorites-title"
      />
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1.5rem' }}>
        <Link to="/woningen" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#C5A059', textDecoration: 'none', marginBottom: '2rem', fontSize: '0.9rem' }}>
          <ArrowLeft size={16} /> Powrót do nieruchomości
        </Link>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#8C8B88', padding: '4rem 0' }}>
            Ładowanie...
          </p>
        ) : favorites.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 1.5rem' }}>
            <Heart size={56} color="#C5A059" style={{ marginBottom: '1rem' }} />
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: '#1A1918', marginBottom: '0.75rem' }}>
              Brak ulubionych
            </h2>
            <p style={{ color: '#585755', marginBottom: '1.5rem' }}>
              Kliknij serce przy nieruchomości, aby zapisać ją tutaj.
            </p>
            <Link to="/woningen" style={{ background: '#C5A059', color: 'white', padding: '0.85rem 1.75rem', textDecoration: 'none', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.82rem' }}>
              Zobacz nieruchomości
            </Link>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }} data-testid="favorites-grid">
            {properties.map(p => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Favorites;
